import { z } from 'zod';
import { reportedBySchema } from './transportExecution.confirmShipmentTransfer.schema';
import { transferTypeSchema } from './transportExecution.availableActions.schema';

const deviationSchema = z.object({
    handling_unit_code: z.string(),
    actual_quantity: z.number().int(),
    planned_quantity: z.number().int(),
    damaged_quantity: z.number().int(),
    description: z.string().optional(),
    handling_unit_content: z
        .array(
            z.object({
                quantity: z.number().int(),
                code: z.string(),
            })
        )
        .optional(),
});

const baselineItemDeviationRequestSchema = z
    .object({
        id: z.string(),
        code: z.string().min(1),
        expected_quantity: z.number().int().nonnegative(),
        actual_quantity: z.number().int().nonnegative(),
        damaged_quantity: z.number().int().nonnegative(),
    })
    .refine(deviation => deviation.damaged_quantity <= deviation.actual_quantity, {
        message: 'damaged_quantity must be less than or equal to actual_quantity',
    });

const additionalItemDeviationRequestSchema = z
    .object({
        code: z.string().min(1),
        actual_quantity: z.number().int().gt(0),
        damaged_quantity: z.number().int().nonnegative(),
    })
    .refine(deviation => deviation.damaged_quantity <= deviation.actual_quantity, {
        message: 'damaged_quantity must be less than or equal to actual_quantity',
    });

export const deviationsReportSchema = z.object({
    comment: z.string().optional(),
    deviations: z.array(deviationSchema),
});

export const reportDeviationsRequestSchema = z.object({
    transferred_at: z.iso.datetime(),
    transfer_type: transferTypeSchema,
    transport_leg_id: z.string(),
    reported_by: reportedBySchema,
    baseline_packaging_id: z.string(),
    comment: z.string().optional(),
    baseline_deviations: z.array(baselineItemDeviationRequestSchema),
    additional_deviations: z.array(additionalItemDeviationRequestSchema),
});

export const reportDeviationsResponseSchema = z.object({
    transfer_confirmation_id: z.string(),
});

const deviationDocumentTypeSchema = z.enum(['PROOF_OF_TRANSFER', 'DOCUMENTATION']);

const deviationDocumentsMetadataSchema = z.object({
    type: deviationDocumentTypeSchema,
    reportedBy: reportedBySchema,
});

export const deviationDocumentsSchema = z.object({
    metadata: deviationDocumentsMetadataSchema,
    file: z.file(),
});

export type ApiReportDeviationsRequest = z.infer<typeof reportDeviationsRequestSchema>;
export type ApiReportDeviationsResponse = z.infer<typeof reportDeviationsResponseSchema>;
export type ApiDeviationsReport = z.infer<typeof deviationsReportSchema>;
